import React from 'react';
import { type AccessSettings } from '../../api/types.js';
import { InputField, SelectField, TextAreaField } from './Field.js';
import ToggleRow from './ToggleRow.js';
import SaveBar from './SaveBar.js';
import { useEditable } from './useEditable.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface Props {
  value: AccessSettings;
  onSave: (next: AccessSettings) => Promise<void>;
}

function AccessTab({ value, onSave }: Props) {
  const { t } = useTranslation();
  const { draft, set, dirty, saving, reset, save } = useEditable(value, onSave);

  const TIMEOUTS = [
    { value: 2, label: t('access.timeout2h') },
    { value: 8, label: t('access.timeout8h') },
    { value: 12, label: t('access.timeout12h') },
    { value: 24, label: t('access.timeout24h') },
  ];

  return (
    <div className="sp on">
      <div className="sp-title">{t('access.title')}</div>
      <div className="sp-subtitle">{t('access.subtitle')}</div>

      <div className="sg">
        <div className="sg-title">{t('access.authentication')}</div>
        <ToggleRow
          label={t('access.mfa')}
          sub={t('access.mfaSub')}
          checked={draft.mfaRequired}
          disabled
          onChange={() => {}}
        />
        <ToggleRow
          label={t('access.sessionBindingToIp')}
          sub={t('access.sessionBindingToIpSub')}
          checked={draft.sessionBindingToIp}
          onChange={(v) => set('sessionBindingToIp', v)}
        />
        <ToggleRow
          label={t('access.forceReAuth')}
          sub={t('access.forceReAuthSub')}
          checked={draft.forceReAuthOnBreach}
          onChange={(v) => set('forceReAuthOnBreach', v)}
        />
      </div>

      <div className="sg">
        <div className="sg-title">{t('access.session')}</div>
        <div className="sg-grid">
          <SelectField
            label={t('access.sessionTimeout')}
            value={draft.sessionTimeoutHours}
            options={TIMEOUTS}
            onChange={(v) => set('sessionTimeoutHours', Number(v))}
          />
          <InputField
            label={t('access.maxConcurrent')}
            type="number"
            min={1}
            value={draft.maxConcurrentSessions}
            onChange={(v) => set('maxConcurrentSessions', Number(v))}
          />
        </div>
      </div>

      <div className="sg">
        <div className="sg-title">{t('access.ipAllowlist')}</div>
        <TextAreaField
          label={t('access.ipAllowlistLabel')}
          value={draft.ipAllowlistCidr.join('\n')}
          rows={4}
          hint={t('access.ipAllowlistHint')}
          onChange={(v) =>
            set(
              'ipAllowlistCidr',
              v
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
        />
      </div>

      <SaveBar dirty={dirty} saving={saving} onCancel={reset} onSave={save} />
    </div>
  );
}

export default AccessTab;
